import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, LinearProgress } from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import FaceRetouchingNaturalOutlinedIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import { fetchSignupJourney } from '../../api/user';
import StatTile from './StatTile';

const KIND = {
  account: { color: '#2E80DB', Icon: PersonAddAltOutlinedIcon },
  login: { color: '#5F6368', Icon: LoginOutlinedIcon },
  step: { color: '#6E59D9', Icon: EditNoteOutlinedIcon },
  photo: { color: '#C77700', Icon: PhotoCameraOutlinedIcon },
  selfie: { color: '#E8710A', Icon: FaceRetouchingNaturalOutlinedIcon },
  status: { color: '#1A73E8', Icon: SwapHorizOutlinedIcon },
  admin: { color: '#D93025', Icon: GavelOutlinedIcon },
};
const OK = '#008E40';
const FAIL = '#D93025';

const SELFIE_TONE = { Approved: OK, Rejected: FAIL, Pending: '#C77700' };

const attemptText = ({ total, failed }) => (total ? `${total}${failed ? ` · ${failed} failed` : ''}` : '0');

const Line = ({ item }) => {
  const { color, Icon } = KIND[item.kind] || KIND.login;
  const tone = item.ok === false ? FAIL : item.ok === true ? OK : color;
  return (
    <Box display="flex" gap={1.5} sx={{ position: 'relative', pb: 2, '&:last-of-type .rail': { display: 'none' } }}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: `${tone}14`, color: tone, display: 'grid', placeItems: 'center' }}>
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box className="rail" sx={{ position: 'absolute', left: 15, top: 34, bottom: -16, width: '2px', bgcolor: '#F0F0F0' }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, pt: '5px' }}>
        <Box display="flex" justifyContent="space-between" gap={2}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: item.ok === false ? FAIL : '#1F1F1F' }}>{item.title}</Typography>
          <Typography sx={{ fontSize: 12, color: '#9AA0A6', whiteSpace: 'nowrap' }}>{moment(item.at).format('hh:mm:ss A')}</Typography>
        </Box>
        {item.detail && <Typography sx={{ fontSize: 13, color: '#5F6368', wordBreak: 'break-word' }}>{item.detail}</Typography>}
        {item.photos?.some((p) => p.url) && (
          <Box display="flex" gap={1.5} sx={{ mt: 1 }}>
            {item.photos.filter((p) => p.url).map((p, i) => (
              <Box key={i} component="a" href={p.url} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', textAlign: 'center' }}>
                <Box component="img" src={p.url} alt={p.label} loading="lazy"
                  sx={{ width: 88, height: 88, objectFit: 'cover', borderRadius: '10px', border: '1px solid #ECECEC', display: 'block', bgcolor: '#F4F4F4' }} />
                <Typography sx={{ fontSize: 11, color: '#6A7282', mt: 0.5 }}>{p.label}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const SignupJourney = ({ userId, refreshMs }) => {
  const [journey, setJourney] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setJourney(null);
    setError(false);
    // A failed refresh keeps the journey on screen; only a failed first load shows the error.
    const load = (first) => fetchSignupJourney(userId)
      .then((j) => live && j && setJourney(j))
      .catch(() => live && first && setError(true));
    load(true);
    const timer = refreshMs ? setInterval(() => !document.hidden && load(false), refreshMs) : null;
    return () => { live = false; clearInterval(timer); };
  }, [userId, refreshMs]);

  if (error) return <Typography sx={{ py: 6, textAlign: 'center', color: '#757575' }}>Couldn't load the signup journey. Please try again.</Typography>;
  if (!journey) {
    return (
      <Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={108} />)}
        </Box>
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="text" height={36} />)}
      </Box>
    );
  }

  const { summary: s, timeline, attemptsRecorded, recordingSince } = journey;
  const progress = Math.round((s.furthestStep.step / s.furthestStep.of) * 100);
  const days = timeline.reduce((acc, item) => {
    const day = moment(item.at).format('DD MMM YYYY');
    (acc[day] = acc[day] || []).push(item);
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 2 }}>
        <StatTile tone="#6E59D9" Icon={EditNoteOutlinedIcon} title="Furthest step" value={`${s.furthestStep.step} of ${s.furthestStep.of}`}
          footer={<>
            <LinearProgress variant="determinate" value={progress}
              sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F3F4', mb: 0.75, '& .MuiLinearProgress-bar': { bgcolor: s.completed ? OK : '#6E59D9' } }} />
            {s.furthestStep.label || 'Not started'}
          </>} />
        <StatTile tone={s.completed ? OK : FAIL} Icon={s.completed ? CheckCircleOutlineIcon : InfoOutlinedIcon}
          title={s.completed ? 'Signup' : 'Stopped at'} value={s.completed ? 'Completed' : (s.stoppedAt || '—')}
          footer={<>Selfie: <Box component="span" sx={{ fontWeight: 600, color: SELFIE_TONE[s.selfieStatus] || '#5F6368' }}>{s.selfieStatus}</Box>
            {s.profileCompletion != null && <> · Profile {s.profileCompletion}%</>}</>} />
        <StatTile tone="#E8710A" Icon={FaceRetouchingNaturalOutlinedIcon} title="Selfie attempts" value={attemptText(s.attempts.selfie)}
          footer={`Photos ${attemptText(s.attempts.photos)} · Steps saved ${s.attempts.steps.total}`} />
        <StatTile tone="#2E80DB" Icon={PersonAddAltOutlinedIcon} title="Signed up"
          value={s.signedUpAt ? moment(s.signedUpAt).format('DD MMM, hh:mm A') : '—'}
          footer={[`with ${s.signupMethod}`, s.firstDevice].filter(Boolean).join(' · ')} />
        <StatTile tone="#5F6368" Icon={LoginOutlinedIcon} title="Sign-ins" value={s.signIns}
          footer={s.lastSeen ? `Last seen ${moment(s.lastSeen).fromNow()}` : 'No sign-in recorded'} />
      </Box>

      {!attemptsRecorded && (
        <Box display="flex" gap={1} alignItems="flex-start" sx={{ mt: 2.5, px: 2, py: 1.5, borderRadius: '10px', bgcolor: '#F4F8FE', border: '1px solid #DCE8FB', color: '#1A4E8A' }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, mt: '2px' }} />
          <Typography sx={{ fontSize: 13 }}>
            Photo, selfie and step attempts are recorded {recordingSince ? `from ${moment(recordingSince).format('DD MMM YYYY, hh:mm A')}` : 'from the latest server update'}.
            This member signed up before that, so the journey shows the steps reached, sign-ins and status changes only.
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        {Object.entries(days).map(([day, items]) => (
          <Box key={day} sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6A7282', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5 }}>{day}</Typography>
            {items.map((item, i) => <Line key={`${item.at}-${i}`} item={item} />)}
          </Box>
        ))}
        {!timeline.length && <Typography sx={{ py: 4, textAlign: 'center', color: '#757575' }}>Nothing recorded for this member yet.</Typography>}
      </Box>
    </Box>
  );
};

export default SignupJourney;
